import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { CrudDataGrid, CrudConfig, CrudField } from './CrudDataGrid';
import { GridRowModel } from '@mui/x-data-grid';
import toast from 'react-hot-toast';
import { apiClient } from '../services/apiClient';
import { ActionButton } from './common';
import {
  DiagnosisIncidenceEneaPercentageReadDTO,
  DiagnosisIncidenceEneaPercentageCreateDTO,
  FunctionalArea,
  IDiagnosisIncidenceEneaPercentageCreateDTO,
} from '../generated/api-client';
import { FUNCTIONAL_AREA_VALUES } from '../utils/functionalArea';
import { useMutation, useQueryClient } from 'react-query';

interface DiagnosisIncidenceEneaPercentagesDialogProps {
  open: boolean;
  diagnosisIncidenceEneaId: string;
  onClose: () => void;
}

// Enum values: P = Processo Produttivo, A = Servizi Ausiliari, G = Servizi Generali
const FUNCTIONAL_AREAS = FUNCTIONAL_AREA_VALUES;

// Tipo per le righe della tabella (una riga per ogni range from/to con tre colonne per le percentuali)
interface PercentageRow {
  id: string;
  from_value: number;
  to_value: number | null;
  processo_produttivo: number;
  servizi_ausiliari: number;
  servizi_generali: number;
  // Store original percentage IDs for updates
  _processo_produttivo_id?: string;
  _servizi_ausiliari_id?: string;
  _servizi_generali_id?: string;
}

export const DiagnosisIncidenceEneaPercentagesDialog: React.FC<
  DiagnosisIncidenceEneaPercentagesDialogProps
> = ({ open, diagnosisIncidenceEneaId, onClose }) => {
  const queryClient = useQueryClient();

  const createMutation = useMutation(
    (data: IDiagnosisIncidenceEneaPercentageCreateDTO) =>
      apiClient.create_item_diagnosis_incidence_enea_percentage__post(data as any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          `diagnosisIncidenceEneaPercentages-${diagnosisIncidenceEneaId}`
        );
      },
    }
  );

  const updateMutation = useMutation(
    (data: { id: string; dto: IDiagnosisIncidenceEneaPercentageCreateDTO }) =>
      apiClient.update_item_diagnosis_incidence_enea_percentage__item_id__put(
        data.id,
        data.dto as any
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          `diagnosisIncidenceEneaPercentages-${diagnosisIncidenceEneaId}`
        );
      },
    }
  );

  const deleteMutation = useMutation(
    (id: string) => apiClient.delete_item_diagnosis_incidence_enea_percentage__item_id__delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          `diagnosisIncidenceEneaPercentages-${diagnosisIncidenceEneaId}`
        );
      },
    }
  );

  // Transform percentages into table rows (grouped by from/to with three percentage columns)
  const tableRows = async () => {
    const groups = new Map<string, Map<string, DiagnosisIncidenceEneaPercentageReadDTO>>();

    const percentages =
      await apiClient.get_by_incidence_enea_id_diagnosis_incidence_enea_percentage_by_incidence_enea__incidence_enea_id__get(
        diagnosisIncidenceEneaId
      );

    percentages.forEach(p => {
      const key = `${p.from_value}_${p.to_value ?? 'null'}`;
      if (!groups.has(key)) {
        groups.set(key, new Map());
      }
      const areaMap = groups.get(key)!;
      if (!areaMap.has(p.functional_area)) {
        areaMap.set(p.functional_area, p);
      }
    });

    const r = Array.from(groups.entries())
      .map(([key, areaMap]) => {
        const firstRow = Array.from(areaMap.values())[0];
        const row: PercentageRow = {
          id: key,
          from_value: firstRow?.from_value ?? 0,
          to_value: firstRow?.to_value ?? null,
          processo_produttivo: areaMap.get('P')?.percentage ?? 0,
          servizi_ausiliari: areaMap.get('A')?.percentage ?? 0,
          servizi_generali: areaMap.get('G')?.percentage ?? 0,
          _processo_produttivo_id: areaMap.get('P')?.id,
          _servizi_ausiliari_id: areaMap.get('A')?.id,
          _servizi_generali_id: areaMap.get('G')?.id,
        };
        return row;
      })
      .sort((a, b) => b.from_value - a.from_value);

    return r;
  };

  const fields: CrudField[] = [
    {
      field: 'from_value',
      headerName: 'Consumi [tep] da',
      width: 150,
      type: 'number',
      editable: true,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'to_value',
      headerName: 'Consumi [tep] a',
      width: 150,
      type: 'number',
      editable: true,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'processo_produttivo',
      headerName: 'Processo Produttivo',
      width: 180,
      type: 'number',
      editable: true,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'servizi_ausiliari',
      headerName: 'Servizi Ausiliari',
      width: 180,
      type: 'number',
      editable: true,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'servizi_generali',
      headerName: 'Servizi Generali',
      width: 180,
      type: 'number',
      editable: true,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  const config: CrudConfig<PercentageRow, any> = {
    queryKey: `diagnosisIncidenceEneaPercentages-${diagnosisIncidenceEneaId}`,
    fields,
    listFn: tableRows,
    createFn: async (data: GridRowModel) => {
      const row = data as PercentageRow;

      // Prima controlla se esistono già record con lo stesso from_value e to_value
      const existingPercentages =
        await apiClient.get_by_incidence_enea_id_diagnosis_incidence_enea_percentage_by_incidence_enea__incidence_enea_id__get(
          diagnosisIncidenceEneaId
        );

      // Crea una mappa per trovare rapidamente i record esistenti
      const existingMap = new Map<string, DiagnosisIncidenceEneaPercentageReadDTO>();
      existingPercentages.forEach(p => {
        if (
          p.from_value === row.from_value &&
          (p.to_value === row.to_value || (p.to_value === null && row.to_value === null))
        ) {
          if (!existingMap.has(p.functional_area)) {
            existingMap.set(p.functional_area, p);
          }
        }
      });

      // Create or update three percentage records (one for each functional area)
      const promises = FUNCTIONAL_AREAS.map(async (area, index) => {
        const percentageField =
          index === 0
            ? 'processo_produttivo'
            : index === 1
              ? 'servizi_ausiliari'
              : 'servizi_generali';
        const percentageValue = row[percentageField as keyof PercentageRow] as number;

        if (percentageValue > 0) {
          const dto: IDiagnosisIncidenceEneaPercentageCreateDTO = {
            incidence_enea_id: diagnosisIncidenceEneaId,
            from_value: row.from_value,
            to_value: row.to_value as any,
            functional_area: area as FunctionalArea,
            percentage: percentageValue,
          };

          const existing = existingMap.get(area);
          if (existing?.id) {
            // Update existing
            return await updateMutation.mutateAsync({
              id: existing.id,
              dto,
            });
          } else {
            // Create new
            return await createMutation.mutateAsync(dto);
          }
        }
        return null;
      });

      await Promise.all(promises.filter(p => p !== null));

      // Return a dummy row for the grid
      const { id, ...rowWithoutId } = row;
      return {
        ...rowWithoutId,
        id: `${row.from_value}_${row.to_value ?? 'null'}`,
      } as PercentageRow;
    },
    updateFn: async (id: string, data: GridRowModel) => {
      const row = data as PercentageRow;

      // Recupera i record esistenti per trovare gli ID
      const existingPercentages =
        await apiClient.get_by_incidence_enea_id_diagnosis_incidence_enea_percentage_by_incidence_enea__incidence_enea_id__get(
          diagnosisIncidenceEneaId
        );

      // Crea una mappa per trovare rapidamente i record esistenti
      const existingMap = new Map<string, DiagnosisIncidenceEneaPercentageReadDTO>();
      existingPercentages.forEach(p => {
        if (
          p.from_value === row.from_value &&
          (p.to_value === row.to_value || (p.to_value === null && row.to_value === null))
        ) {
          if (!existingMap.has(p.functional_area)) {
            existingMap.set(p.functional_area, p);
          }
        }
      });

      // Update or create three percentage records
      const updates = FUNCTIONAL_AREAS.map(async (area, index) => {
        const percentageField =
          index === 0
            ? 'processo_produttivo'
            : index === 1
              ? 'servizi_ausiliari'
              : 'servizi_generali';
        const percentageValue = row[percentageField as keyof PercentageRow] as number;

        const dto: IDiagnosisIncidenceEneaPercentageCreateDTO = {
          incidence_enea_id: diagnosisIncidenceEneaId,
          from_value: row.from_value,
          to_value: row.to_value as any,
          functional_area: area as FunctionalArea,
          percentage: percentageValue,
        };

        const existing = existingMap.get(area);
        if (existing?.id) {
          // Update existing
          return await updateMutation.mutateAsync({
            id: existing.id,
            dto,
          });
        } else if (percentageValue > 0) {
          // Create new
          return await createMutation.mutateAsync(dto);
        }
        return null;
      });

      await Promise.all(updates.filter(u => u !== null));

      return row;
    },
    deleteFn: async (id: string) => {
      // Find the row to get the IDs
      const row = (await tableRows()).find(r => r.id === id);
      if (!row) return;

      // Delete all three percentage records
      const deletes = [
        row._processo_produttivo_id,
        row._servizi_ausiliari_id,
        row._servizi_generali_id,
      ]
        .filter((id): id is string => !!id)
        .map(id => deleteMutation.mutateAsync(id));

      await Promise.all(deletes);
    },
    getId: (row: PercentageRow) => row.id,
    getCreateDto: (row: GridRowModel) => {
      const r = row as PercentageRow;
      return {
        from_value: Number(r.from_value) || 0,
        to_value: r.to_value ? Number(r.to_value) : null,
        processo_produttivo: Number(r.processo_produttivo) || 0,
        servizi_ausiliari: Number(r.servizi_ausiliari) || 0,
        servizi_generali: Number(r.servizi_generali) || 0,
      };
    },
    getDefaultValues: () => ({
      from_value: 0,
      to_value: null,
      processo_produttivo: 0,
      servizi_ausiliari: 0,
      servizi_generali: 0,
    }),
  };

  const gridRef = React.useRef<{ addRow: () => void }>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Gestione Percentuali</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={onClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <CrudDataGrid ref={gridRef} config={config} height={500} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', py: 1 }}>
          <ActionButton
            icon={<AddIcon />}
            tooltip="Aggiungi riga"
            onClick={() => gridRef.current?.addRow()}
            variant="filled"
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
